import '../portal/portalTheme.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/backendURL';
import { ArrowRight, CheckCircle2, Loader2, Phone, MessageCircle, Clock, Shield, Lock, Heart, ChevronDown } from 'lucide-react';

// Inline Lexa logo
const FunnelLogo = () => (
  <a href="/" className="flex items-center gap-2 group" data-testid="funnel-logo">
    <img
      src="/assets/footer-icon.png"
      alt="Lexa.run"
      className="h-9 w-9 object-contain"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
    <span
      className="text-xl font-bold bg-clip-text text-transparent"
      style={{ backgroundImage: 'linear-gradient(to right, #9333ea, #3b82f6)' }}
    >
      Lexa.run
    </span>
  </a>
);

/**
 * TalkToHumanFunnel, Funnel #3 (Retargeting / Win-back)
 * Path: /talk-to-human
 * Lower-friction, higher-empathy offer for users who abandoned funnels #1 or #2.
 * Minimal contact form: email + question.
 * Sends to demo_requests with source='funnel-talk-to-human' so OPS dashboard sees it.
 */
export default function TalkToHumanFunnel() {
  const [email, setEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "Talk to the Lexa Team | Lexa.run";
  }, []);

  const getUtm = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email so we can reply.');
      return;
    }
    setSubmitting(true);
    try {
      // Reuse the demo endpoint with a distinct source, lands in the same OPS dashboard
      await axios.post(`${BACKEND_URL}/api/funnel/demo`, {
        name: name.trim() || 'Anonymous',
        email: email.trim(),
        company: 'Retargeting lead',
        biggest_qa_pain: question.trim() || 'No question provided, wants to talk first.',
        phone: phone.trim() || undefined,
        preferred_time: 'Contact requested',
        source: 'funnel-talk-to-human',
        ...getUtm(),
      });
      setSubmitted(true);
      setTimeout(() => document.getElementById('thanks')?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lexa-portal lexa-portal-content min-h-screen antialiased" data-testid="funnel-talk-to-human">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <FunnelLogo />
          <a href="/get-started" data-testid="header-cta" className="hidden sm:inline-flex items-center gap-2 rounded-full border border-slate-200 hover:border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-all">
            Or try it free instead <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </header>

      {/* Soft empathy strip, not urgency. This is a win-back. */}
      <div className="bg-slate-950 text-white text-center py-2.5 text-sm font-medium">
        <Heart className="inline h-4 w-4 mr-1.5 -mt-0.5 text-pink-400" />
        Not ready to sign up yet? We get it. Let's just <b>talk first</b>.
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(124,58,237,0.18),transparent_70%)]" />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-pink-200 bg-pink-50 px-3 py-1 text-xs font-semibold text-pink-700 mb-6">
            <MessageCircle className="h-3.5 w-3.5" /> NO PITCH · NO PRESSURE · NO CALENDAR ROULETTE
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Got Questions About <br className="hidden sm:block" />
            Lexa? <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600">Ask a Real Human.</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto">
            You're not ready to sign up, watch a demo, or commit. That's fine. Drop your question
            below and our team will follow up by email.
          </p>

          {/* Trust line */}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-5 sm:gap-8 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Senior engineer, not sales</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber-500" /> Email follow-up</span>
            <span className="inline-flex items-center gap-1.5"><Shield className="h-4 w-4 text-blue-500" /> Zero-pressure conversation</span>
          </div>

          {/* Form */}
          {!submitted ? (
            <form onSubmit={handleSubmit} data-testid="talk-form" className="mt-10 mx-auto max-w-xl rounded-3xl border border-slate-200 bg-white p-7 shadow-2xl shadow-purple-500/10 text-left">
              <h2 className="text-xl font-extrabold text-slate-900">What's on your mind?</h2>
              <p className="text-sm text-slate-500 mt-1.5">Tell us as much (or as little) as you'd like. We'll figure it out.</p>

              <div className="mt-5 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    data-testid="talk-form-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                    autoComplete="name"
                  />
                  <input
                    data-testid="talk-form-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email *"
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                    autoComplete="email"
                  />
                </div>
                <input
                  data-testid="talk-form-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone (optional, if you'd rather we call)"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                  autoComplete="tel"
                />
                <textarea
                  data-testid="talk-form-question"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="What would you ask if you had a senior QA engineer for 5 minutes? (e.g., 'Will this work with our web app?', 'How is this different from Cypress?')"
                  rows={4}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none resize-none"
                />
              </div>

              {error && <p data-testid="talk-form-error" className="mt-3 text-sm text-rose-600">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                data-testid="talk-form-submit"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (<><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>) : (<>Send My Question <ArrowRight className="h-5 w-5" /></>)}
              </button>

              <p className="text-[11px] text-slate-400 mt-3 text-center leading-relaxed">
                <Lock className="inline h-3 w-3 mr-1 -mt-0.5" />
                Real human reply. No marketing emails. Your data stays private.
              </p>
            </form>
          ) : (
            <div id="thanks" data-testid="talk-form-success" className="mt-10 mx-auto max-w-xl rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 shadow-2xl shadow-emerald-500/10 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500 flex items-center justify-center text-white mb-4">
                <CheckCircle2 className="h-9 w-9" />
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900">Your question is in. 💌</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                We received your question. Our team will follow up at <b>{email}</b>.
              </p>
              <div className="mt-6 grid sm:grid-cols-2 gap-3 text-left text-sm">
                <a href="/get-started" data-testid="success-cta-signup" className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-purple-300 hover:shadow-md transition group">
                  <div className="font-bold text-slate-900 group-hover:text-purple-700">→ Try Lexa free in the meantime</div>
                  <div className="text-slate-500 mt-1">Explore the free plan.</div>
                </a>
                <a href="/book-demo" data-testid="success-cta-demo" className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-purple-300 hover:shadow-md transition group">
                  <div className="font-bold text-slate-900 group-hover:text-purple-700">→ Request a product demo</div>
                  <div className="text-slate-500 mt-1">Explore the product with our team.</div>
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* What people ask */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 text-center tracking-tight">
            Questions to Explore
          </h2>
          <p className="mt-3 text-slate-600 text-center">Learn about the product before deciding whether it fits your team.</p>
          <div className="mt-10 space-y-3">
            {[
              { q: "What can I test with Lexa?", a: "Lexa includes API testing, browser flow testing, test suites, and Sprint QA. Tell us about your application so we can discuss whether it fits." },
              { q: "How does AI fit into testing?", a: "AI can assist with test creation. You should review generated tests, assertions, and results for your application." },
              { q: "Can we discuss deployment and security requirements?", a: "Yes. Share your requirements with us so we can confirm what is currently supported before you commit." },
              { q: "Where can I see prices and limits?", a: "Visit the Pricing page for current published plans and limits, or contact us to discuss your requirements." },
            ].map((item, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  data-testid={`talk-faq-toggle-${i}`}
                  className="w-full flex items-center justify-between text-left px-5 py-4 font-semibold text-slate-900 hover:bg-slate-50 transition"
                >
                  <span>{item.q}</span>
                  <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-4 text-slate-600 leading-relaxed">{item.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA, soft, exploratory */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-violet-600 to-blue-600 text-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            One question. <br className="hidden sm:block" />
            One real human reply. <br className="hidden sm:block" />
            Zero strings.
          </h2>
          <p className="mt-4 text-purple-100 text-lg">No demo. No signup. Just answers.</p>
          <a href="#thanks" onClick={(e) => { e.preventDefault(); document.querySelector('form')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="final-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-purple-700 px-8 py-4 text-base font-bold shadow-xl hover:-translate-y-0.5 hover:shadow-2xl transition-all">
            Ask Your Question Now <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 text-xs py-6 text-center px-4">
        © {new Date().getFullYear()} Lexa.run · <a href="/privacy" className="hover:text-white">Privacy</a> · <a href="/terms" className="hover:text-white">Terms</a>
      </footer>
    </div>
  );
}
